import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    TextField, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton, Chip
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Store as StoreIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { formatAmountForDisplayWithPreferences, formatDateWithTimezone } from 'utils';
import { usePreferences } from 'hooks/usePreferences';

// Status chip component
function CreditMemoStatusChip({ status }) {
    const getStatusColor = (status) => {
        switch (status) {
            case 'pending': return 'warning';
            case 'approved': return 'success';
            case 'applied': return 'info';
            case 'cancelled': return 'error';
            default: return 'default';
        }
    };

    const displayStatus = status || 'pending';

    return (
        <Chip 
            label={displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)} 
            color={getStatusColor(displayStatus)} 
            size="small" 
        />
    );
}

// Helper component for the Add/Edit Credit Memo Dialog
function AddEditCreditMemoDialog({ open, onClose, creditMemoData, onSave, maxCreditAmount = 0, invoiceData }) {
    const [creditAmount, setCreditAmount] = useState('');
    const [reason, setReason] = useState('');
    const [authorizedBy, setAuthorizedBy] = useState('');
    const [notes, setNotes] = useState('');
    const [errors, setErrors] = useState({});

    // Populate form when creditMemoData changes (for editing)
    useEffect(() => {
        if (open && creditMemoData) {
            setCreditAmount(creditMemoData.creditAmount || '');
            setReason(creditMemoData.reason || '');
            setAuthorizedBy(creditMemoData.authorizedBy || '');
            setNotes(creditMemoData.notes || '');
        } else if (open) {
            // Reset form for new credit memo
            setCreditAmount('');
            setReason('');
            setAuthorizedBy('');
            setNotes('');
        }
        setErrors({}); // Clear errors on open/data change
    }, [open, creditMemoData]);

    // Validate form data
    const validate = () => {
        const newErrors = {};

        if (!creditAmount || isNaN(creditAmount) || parseFloat(creditAmount) <= 0) {
            newErrors.creditAmount = 'Valid credit amount is required';
        } else if (parseFloat(creditAmount) > maxCreditAmount) {
            newErrors.creditAmount = `Credit amount cannot exceed $${maxCreditAmount.toFixed(2)}`;
        }

        if (!reason || reason.trim().length < 3) {
            newErrors.reason = 'Reason is required (minimum 3 characters)';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (!validate()) return;
        
        const creditMemoDataToSave = {
            originalInvoiceId: invoiceData?.id,
            creditAmount: parseFloat(creditAmount),
            reason: reason.trim(),
            authorizedBy: authorizedBy.trim() || null,
            notes: notes.trim() || null,
            locationId: invoiceData?.location?.id
        };

        if (creditMemoData) {
            creditMemoDataToSave.id = creditMemoData.id;
        }

        onSave(creditMemoDataToSave);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {creditMemoData ? 'Edit Credit Memo' : 'Create Store Credit'}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    {/* Credit Amount */}
                    <TextField
                        label="Credit Amount"
                        type="text"
                        value={creditAmount}
                        onChange={(e) => setCreditAmount(e.target.value)}
                        error={!!errors.creditAmount}
                        helperText={errors.creditAmount || `Maximum creditable: $${maxCreditAmount.toFixed(2)}`}
                        fullWidth
                        required
                        inputProps={{ step: '0.01', min: '0' }}
                    />

                    {/* Reason */}
                    <TextField
                        label="Reason for Credit"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        error={!!errors.reason}
                        helperText={errors.reason}
                        fullWidth
                        required
                        multiline
                        rows={3}
                        placeholder="Please provide a detailed reason for the store credit..."
                    />

                    {/* Authorized By */}
                    <TextField
                        label="Authorized By"
                        value={authorizedBy}
                        onChange={(e) => setAuthorizedBy(e.target.value)}
                        fullWidth
                        placeholder="Manager name or authorization code"
                    />

                    {/* Notes */}
                    <TextField
                        label="Additional Notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        fullWidth
                        multiline
                        rows={2}
                        placeholder="Any additional notes about this store credit..."
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave} variant="contained" color="primary">
                    {creditMemoData ? 'Update Credit Memo' : 'Create Store Credit'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// Main CreditMemoCollector component
const CreditMemoCollector = ({ 
    creditMemos = [], 
    onAddCreditMemo, 
    onEditCreditMemo, 
    onCancelCreditMemo, 
    onUpdateCreditMemoStatus,
    disabled = false,
    maxCreditAmount = 0,
    invoiceData
}) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingCreditMemo, setEditingCreditMemo] = useState(null);
    const { preferences } = usePreferences();

    const totalStoreCredit = useMemo(() => {
        return creditMemos
            .filter(memo => memo.status === 'approved' && memo.status !== 'applied' && memo.status !== 'cancelled')
            .reduce((sum, memo) => sum + (parseFloat(memo.creditAmount) || 0), 0);
    }, [creditMemos]);

    const handleAddClick = () => {
        setEditingCreditMemo(null);
        setDialogOpen(true);
    };

    const handleEditClick = (creditMemo) => {
        setEditingCreditMemo(creditMemo);
        setDialogOpen(true);
    };

    const handleSave = (creditMemoData) => {
        if (editingCreditMemo) {
            onEditCreditMemo(creditMemoData);
        } else {
            onAddCreditMemo(creditMemoData);
        }
    };

    const handleStatusUpdate = (creditMemoId, newStatus) => {
        onUpdateCreditMemoStatus(creditMemoId, newStatus);
    };

    const handleCancel = (creditMemoId) => {
        //const reason = prompt('Please provide a reason for cancelling this credit memo:');
        //if (reason && reason.trim()) {
            onCancelCreditMemo(creditMemoId, "cancelled by user");
        //}
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StoreIcon />
                    Store Credits
                    {totalStoreCredit > 0 && (
                        <Chip 
                            label={`Available: ${formatAmountForDisplayWithPreferences(totalStoreCredit, preferences)}`} 
                            color="primary" 
                            size="small" 
                        />
                    )}
                </Typography>
                <Button
                    startIcon={<AddIcon />}
                    onClick={handleAddClick}
                    variant="outlined"
                    color="primary"
                    size="small"
                    disabled={disabled || maxCreditAmount <= 0}
                >
                    Create Store Credit
                </Button>
            </Box>

            {creditMemos.length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: 'center' }}>
                    No store credits issued
                </Typography>
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Credit Memo #</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>Amount</TableCell>
                                <TableCell>Reason</TableCell>
                                {/* <TableCell>Status</TableCell> */}
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {creditMemos.map((creditMemo) => (
                                <TableRow key={creditMemo.id || creditMemo.tempId}>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="bold">
                                            {creditMemo.creditMemoNumber || 'Pending...'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {formatDateWithTimezone(creditMemo.creditDate)}
                                    </TableCell>
                                    <TableCell>
                                        <Typography color="primary" fontWeight="bold">
                                            {formatAmountForDisplayWithPreferences(creditMemo.creditAmount, preferences)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography 
                                            variant="body2" 
                                            sx={{ 
                                                maxWidth: 200, 
                                                overflow: 'hidden', 
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                            title={creditMemo.reason}
                                        >
                                            {creditMemo.reason}
                                        </Typography>
                                    </TableCell>
                                    {/* <TableCell>
                                        <CreditMemoStatusChip status={creditMemo.status} />
                                    </TableCell> */}
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                            {creditMemo.status === 'pending' && (
                                                <>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleEditClick(creditMemo)}
                                                        disabled={disabled}
                                                        title="Edit credit memo"
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleStatusUpdate(creditMemo.id, 'approved')}
                                                        disabled={disabled}
                                                        color="success"
                                                        title="Approve credit memo"
                                                    >
                                                        ✓
                                                    </IconButton>
                                                </>
                                            )}
                                            <IconButton
                                                size="small"
                                                onClick={() => handleCancel(creditMemo.id || creditMemo.tempId)}
                                                disabled={disabled}
                                                color="error"
                                                title="Cancel credit memo"
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>               

                                            {/* {creditMemo.status === 'approved' && (
                                                <Typography variant="body2" color="success.main">
                                                    <CheckCircleIcon fontSize="small" />
                                                </Typography>
                                            )} */}
                                            
                                            {creditMemo.status === 'applied' && (
                                                <Typography variant="body2" color="info.main">
                                                    Used
                                                </Typography>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <AddEditCreditMemoDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                creditMemoData={editingCreditMemo}
                onSave={handleSave}
                maxCreditAmount={maxCreditAmount}
                invoiceData={invoiceData}
            />
        </Box>
    );
};

export default CreditMemoCollector;
